import { Locale, i18nConfig } from '@/project.config';

// EWM-522 — the single source for each locale's deployed host.
//
// Each AU/NZ storefront is published on its own hostname
// (dev.electusdistribution.com.au / dev.electusdistribution.co.nz; uat + prod
// differ), supplied per-environment via NEXT_PUBLIC_HOST_AU / NEXT_PUBLIC_HOST_NZ.
// When a locale's host is set, the deployed site serves PREFIX-LESS URLs (the
// domain implies the locale); when unset — localhost and any env without the
// vars — the locale falls back to the shared origin under a `/{locale}` path
// prefix.
//
// Both the next-intl `domains` routing config (src/i18n/routing.ts) and the SEO
// URL emitters (src/utils/lib/seo-tools) read from here, so the host→locale
// mapping lives in exactly ONE place — routing + SEO can't drift.
//
// Setting NEXT_PUBLIC_HOST_AU/NZ flips BOTH planes to prefix-less per-domain URLs
// at once. Leave them UNSET on localhost / the shared origin to keep the
// `/{locale}` prefix (the behaviour-preserving default).
//
// EWM-862 — each var holds a COMMA-SEPARATED LIST of hosts, not a single host.
// One locale is legitimately reachable on several hostnames: the canonical
// `www.` host, the bare apex, and (during a migration) an old host like `shop.`.
// Every one of them must map to the SAME locale.
//
// Why a list and not a derived apex: at the www cutover the map held only the two
// `www.` hosts, so `electusdistribution.co.nz` matched NOTHING, next-intl fell
// back to `defaultLocale` (en-AU), and NZ visitors landed on the AUSTRALIAN
// storefront priced in AUD. Stripping `www.` to synthesise the apex would have
// fixed that one case while still silently mispricing the next host we add
// (`shop.`, a vanity domain, a staging alias). The list makes "which hosts serve
// this country" a per-environment CONFIG decision with no host-shaped heuristic
// in the code, so adding a host is an env change and can never resolve to the
// wrong locale by omission.
//
// The FIRST entry is CANONICAL — it's what the SEO emitters advertise (canonical
// links, hreflang, sitemap URLs) and what next-intl redirects cross-locale
// navigation to. Order matters: put the `www.` host first. Additional entries are
// accepted and served, never advertised.
const HOSTS_BY_LOCALE: Record<Locale, string | undefined> = {
  'en-AU': process.env.NEXT_PUBLIC_HOST_AU,
  'en-NZ': process.env.NEXT_PUBLIC_HOST_NZ,
};

/**
 * Every bare hostname that serves a locale, canonical FIRST, in the order given
 * by NEXT_PUBLIC_HOST_*. Empty on shared-origin environments (localhost).
 * Protocol + trailing slash are stripped so each value is usable directly as a
 * next-intl `domains[].domain`.
 */
export const localeDomainHosts = (locale: string): string[] =>
  (HOSTS_BY_LOCALE[locale as Locale] ?? '')
    .split(',')
    .map((raw) =>
      raw
        .trim()
        .replace(/^https?:\/\//, '')
        .replace(/\/+$/, ''),
    )
    .filter(Boolean);

/**
 * The CANONICAL hostname for a locale (e.g. `www.electusdistribution.com.au`),
 * or `undefined` on shared-origin environments (localhost). This is the host the
 * SEO layer advertises — additional hosts from NEXT_PUBLIC_HOST_* are served but
 * never emitted. Identical to the old single-value behaviour when the env var
 * holds one host.
 */
export const localeDomainHost = (locale: string): string | undefined => localeDomainHosts(locale)[0];

/**
 * EWM-862 — given a request host, the CANONICAL host it should be redirected to,
 * or `undefined` to serve it as-is.
 *
 * Returns a target only for a host that is CONFIGURED for a locale but is not
 * that locale's canonical (first) entry — i.e. the bare apex, or a legacy `shop.`
 * alias. The canonical host itself returns `undefined`, so there is no loop.
 *
 * Hosts absent from NEXT_PUBLIC_HOST_* are left ALONE, deliberately: Netlify
 * deploy previews (`deploy-preview-12--jeg-prod.netlify.app`), the
 * `*.netlify.app` subdomain and `localhost` must keep serving, so this is an
 * allowlist-driven redirect and never a blanket "not canonical → bounce".
 *
 * Why this lives in code and not netlify.toml: `[[redirects]]` has no Host
 * condition, so the only way to match a hostname there is a full-URL `from`
 * (`https://electusdistribution.co.nz/*`). Those rules were deployed at the www
 * cutover and verified NOT to fire — the apex kept being served by the Next
 * handler. The proxy sees the real Host header, so this is the reliable place.
 */
export const canonicalHostRedirect = (host: string | undefined): string | undefined => {
  const bare = host
    ?.replace(/^https?:\/\//, '')
    .replace(/\/+$/, '')
    .replace(/:\d+$/, '');
  if (!bare) return undefined;

  for (const locale of i18nConfig.locales) {
    const hosts = localeDomainHosts(locale);
    // Index 0 is canonical — already correct, nothing to do.
    if (hosts.length > 1 && hosts.indexOf(bare) > 0) return hosts[0];
  }
  return undefined;
};

/**
 * Absolute origin for a locale's dedicated domain (`https://<host>`), or
 * `undefined` on shared-origin environments. Used to build absolute SEO URLs.
 */
export const localeDomainOrigin = (locale: string): string | undefined => {
  const host = localeDomainHost(locale);
  return host ? `https://${host}` : undefined;
};

/** True when this locale is served on its own domain (prefix-less URLs). */
export const isDomainHosted = (locale: string): boolean => !!localeDomainHost(locale);

/**
 * EWM-537 — locales served by a given request host. On a per-locale domain
 * (NEXT_PUBLIC_HOST_AU/NZ set) the host maps to exactly that one locale, so the
 * sitemap index lists only that domain's children — the .co.nz domain never
 * advertises .com.au sitemaps, and vice versa. On the shared origin (localhost
 * / no per-locale hosts, or any host that matches none) it falls back to ALL
 * locales, which are served under `/{locale}/` there.
 */
export const localesForHost = (host: string | undefined): readonly Locale[] => {
  // Normalise the request host: drop any protocol, trailing slash, and `:port`
  // (an edge proxy could forward `host:443`) before matching the configured hosts.
  const bare = host
    ?.replace(/^https?:\/\//, '')
    .replace(/\/+$/, '')
    .replace(/:\d+$/, '');
  // EWM-862 — match against EVERY host configured for the locale (apex, `shop.`,
  // …), not just the canonical one, so a sitemap fetched on a non-canonical host
  // still advertises exactly that country's children. The URLs inside remain
  // canonical (built from localeDomainOrigin), so a crawler arriving on the apex
  // is pointed at `www.` rather than being told the apex is authoritative.
  const matched = bare ? i18nConfig.locales.filter((locale) => localeDomainHosts(locale).includes(bare)) : [];
  return matched.length ? matched : i18nConfig.locales;
};
